import { createSelector } from '@ngrx/store';
import { selectPayrollRule } from './payroll.rule.selectors';
import {
  selectPayrollExtraByName,
  selectPayrollCube,
} from './payroll.selectors';
import * as _ from 'lodash';
import { PayrollSyntheseEntry } from '../payroll.types';
import { PayrollRuleEntry } from 'app/api/payroll.rule.api.service';
import { PayrollEntry } from 'app/api/payroll.api.service';

export const selectPayrollSynthese = (name: string) =>
  createSelector(selectPayrollExtraByName(name), (extras) => {
    if (extras) {
      const synthesis = extras.choices;
      return _.map(synthesis, (choice) => {
        return { name, choice };
      });
    }
    return [];
  });

export const selectPayrollSyntheseBy = (
  formatType: string,
  viewType: string,
  entries?: any[]
) =>
  createSelector(
    selectPayrollSynthese(viewType),
    selectPayrollRule,
    selectPayrollCube,
    (
      synthesis: {
        name: string;
        choice: string;
        type?: string;
        items: PayrollSyntheseEntry[];
      }[],
      payrollRule: PayrollRuleEntry[],
      payrolls: PayrollEntry[]
    ) => {
      const countValueByName = {};
      const valueByName: any = {};
      const totalValueByTimeline: { [timeline: string]: number } = {};
      let synthesisView = [];

      _.forEach(
        payrolls,
        ({
          matricule,
          timeline,
          fullRawCharges,
          name,
          ana2Axis,
          extraAxis,
        }) => {
          if (!totalValueByTimeline[timeline]) {
            totalValueByTimeline[timeline] = 0;
          }
          if (!valueByName[matricule]) {
            valueByName[matricule] = {
              matricule,
              items: [],
              ana2Axis,
              extraAxis,
            };
          }
          if (!countValueByName[timeline]) {
            countValueByName[timeline] = {};
          }
          if (!countValueByName[timeline][matricule]) {
            Object.assign(countValueByName[timeline], { [matricule]: 0 });
          }
          totalValueByTimeline[timeline] += fullRawCharges;
          countValueByName[timeline][matricule]++;

          valueByName[matricule].items.push({
            timeline,
            key: matricule,
            name,
            value: fullRawCharges,
          });
        }
      );

      if (viewType === 'Analytics' && entries) {
        synthesisView = _.cloneDeep(entries);
      } else {
        synthesis = _.map(synthesis, (synth) => {
          synth.items = [];
          _.forEach(payrollRule, (rule) => {
            if (synth.name !== '-- Nouveau --') {
              if (
                synth.name === rule.payrollExtraKey &&
                synth.choice === rule.payrollExtraValue
              ) {
                synth.items = [
                  ...synth.items,
                  ...valueByName[rule.payrollKey].items,
                ];
              }
            }
          });
          return synth;
        }).filter((synthese) => synthese.items.length > 0);

        // Compute Not Affected Value
        const NA = {
          choice: 'N/A',
          name: 'N/A',
          items: [],
          type: '',
        };

        const NAByTimeline: {
          [timeline: string]: {
            timeline: string;
            value: number;
            totalByTimeline: number;
            count: number;
          };
        } = {};

        synthesis.forEach((synth) => {
          synth.items.forEach((item) => {
            if (!NAByTimeline.hasOwnProperty(item.timeline)) {
              NAByTimeline[item.timeline] = {
                timeline: item.timeline,
                value: totalValueByTimeline[item.timeline] - item.value,
                totalByTimeline: totalValueByTimeline[item.timeline],
                count: 0,
              };
            } else {
              NAByTimeline[item.timeline].value -= item.value;
            }
            NAByTimeline[item.timeline].count++;
          });
        });

        NA.items = _.flatten(Object.values(NAByTimeline));
        synthesis.push(NA);
        synthesisView = synthesis;
      }

      switch (formatType) {
        case 'percentage':
          return _.map(synthesisView, (synth) => {
            const items = {};
            _.forEach(synth.items, (item) => {
              if (!items[item.timeline]) {
                items[item.timeline] = {
                  timeline: item.timeline,
                  value: 0,
                  totalByTimeline: totalValueByTimeline[item.timeline],
                };
              }

              let value =
                (item.value * 100) / totalValueByTimeline[item.timeline];

              if (viewType === 'Analytics') {
                if (item.percent) {
                  value =
                    (item.value * item.percent) /
                    totalValueByTimeline[item.timeline];
                }
              }

              items[item.timeline].value += value;
            });
            synth.items = _.flatten(Object.values(items));
            synth.type = formatType;
            return synth;
          });
        case 'person':
          return _.map(synthesisView, (synth) => {
            const items = {};
            _.forEach(synth.items, (item) => {
              if (!items[item.timeline]) {
                items[item.timeline] = {
                  timeline: item.timeline,
                  value: 0,
                  totalByTimeline: totalValueByTimeline[item.timeline],
                  count: Object.values(countValueByName[item.timeline]).length,
                };
              }

              if (viewType === 'Analytics') {
                if (item.percent) {
                  item.value = item.percent / 100;
                }
              } else {
                if (item.name) {
                  item.value = 1;
                  items[item.timeline].value += item.value;
                } else {
                  items[item.timeline].value =
                    items[item.timeline].count - item.count;
                }
              }
            });
            synth.items = _.flatten(Object.values(items));
            synth.type = formatType;
            return synth;
          });
        case 'cash':
        default:
          return _.map(synthesisView, (synth) => {
            const items = {};
            _.forEach(synth.items, (item) => {
              if (!items[item.timeline]) {
                items[item.timeline] = {
                  timeline: item.timeline,
                  value: 0,
                  totalByTimeline: totalValueByTimeline[item.timeline],
                };
              }

              if (viewType === 'Analytics') {
                if (item.percent) {
                  item.value = (item.value * item.percent) / 100;
                }
              }

              items[item.timeline].value += Math.round(item.value);
            });
            synth.items = _.flatten(Object.values(items));
            synth.type = formatType;
            return synth;
          });
      }
    }
  );
